import React, { useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ScanMulti } from './models';
import { getRelativeDate, parseAsPacific, parseAsUTC } from '../../../../utils/time';
import { CitationDetailsModal } from './CitationDetailsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { preprocessContentForCitations, preprocessTimestamps } from './utils';

interface LLMDashboardMarkdownRendererProps {
    content: string;
    scanMulti?: ScanMulti;
}

const CitationBubble: React.FC<{
    dataType: string;
    displayIndex: string;
    citationNumber: string;
    citationMetadata?: Record<string, string>;
    scanMulti?: ScanMulti;
}> = ({ dataType, displayIndex, citationNumber, citationMetadata, scanMulti }) => {

    const [showPopover, setShowPopover] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedCitationNumber, setSelectedCitationNumber] = useState<string>();
    const timeoutRef = useRef<any>(null);

    const getFormattedMetadata = (citationMetadata?: Record<string, string>) => {
        let title: string | undefined;
        let author: string | undefined;
        let description: string | undefined;
        let date: string | undefined;

        if (citationMetadata) {
            switch (dataType) {
                case "order":
                    title = "Order";
                    description = "";
                    date = citationMetadata.order_time;
                    break;

                case "note":
                    title = citationMetadata.note_type;
                    description = citationMetadata.note_service;
                    date = citationMetadata.note_date;
                    break;

                case "home_med":
                case "home_meds":
                case "home_medication":
                    title = "Home Medications";
                    description = "";
                    date = citationMetadata.order_date;
                    break;

                case "reason_for_visit":
                    title = "Reason for Visit";
                    description = "";
                    date = "";
                    break;

                case "vital_signs_summary":
                    title = "Vital Signs Summary";
                    description = `From ${citationMetadata.start_time} to ${citationMetadata.end_time}`;
                    date = "";
                    break;

                case "vital_1min":
                    title = "Vital Signs (1 min)";
                    description = "";
                    date = citationMetadata.numeric_time;
                    break;

                case "rhythm_summary":
                    title = "Rhythm Summary";
                    description = "";
                    date = "";
                    break;
                case 'rhythm_episode':
                    title = "Rhythm Episode";
                    description = `${citationMetadata.rhythm} (${citationMetadata.duration} sec)`;
                    date = citationMetadata.start_time;
                    break;
                case "risk_prediction":
                    title = citationMetadata.model;
                    description = `Calibrated Prediction: ${citationMetadata.calibrated_prediction}; Percentile: ${citationMetadata.percentile}`;
                    date = citationMetadata.numeric_time;
                    break;

                case "risk_feature":
                    title = citationMetadata.model;
                    description = `Risk Feature: ${citationMetadata.feature_name} with importance ${citationMetadata.importance}`;
                    date = "";
                    break;

                case "imaging":
                    title = citationMetadata.study;
                    description = citationMetadata.modality;
                    date = citationMetadata.result_time;
                    break;

                case "lab":
                case "lab_result":
                    title = "Lab Panel";
                    description = "";
                    date = citationMetadata.result_time;
                    break;

                case "medication":
                    title = citationMetadata.med_name;
                    description = "Medication administered";
                    date = citationMetadata.action_time;
                    break;

                case "patient_demographics":
                    title = "Patient Demographic";
                    description = `Age: ${citationMetadata.age}; Gender: ${citationMetadata.gender}`;
                    date = "";
                    break;

                case "encounter":
                    title = "Hospital Encounter";
                    description = `Hospital encounter occurred at ${citationMetadata.arrival_date}`;
                    date = citationMetadata.arrival_date;
                    break;

                case "echo":
                    title = "Echo";
                    description = citationMetadata.study;
                    date = citationMetadata.result_time;
                    break;

                case "ecg":
                    title = "ECG";
                    description = citationMetadata.study;
                    date = citationMetadata.result_time;
                    break;

                // add other citation types here as needed
                default:
                    description = JSON.stringify(citationMetadata, null, 2);
            }
        }

        date = date ? parseAsUTC(date).toLocaleString() : date;

        return {
            title,
            author,
            description,
            date
        }
    }

    const {
        title, description, author, date
    } = getFormattedMetadata(citationMetadata);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setShowPopover(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setShowPopover(false);
        }, 500); // keep tooltip open long enough to move into the popover
    };

    const handlePopoverMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setShowPopover(true);
    };

    const handlePopoverMouseLeave = () => {
        setShowPopover(false);
    };

    const renderPopoverContent = () => {
        if (citationMetadata) {
            return (
                <Popover
                    id={`citation-popover-${citationNumber}`}
                    className="citation-popover"
                    onMouseEnter={handlePopoverMouseEnter}
                    onMouseLeave={handlePopoverMouseLeave}
                    onClick={(e: any) => {
                        e.stopPropagation();
                        setSelectedCitationNumber(citationNumber);
                        setShowModal(true);
                    }}
                >
                    <Popover.Header className="citation-popover-header">
                        {title}
                    </Popover.Header>
                    <Popover.Body className="citation-popover-body">
                        {author && (
                            <div className="citation-author">
                                <strong>Author: </strong> {author}
                            </div>
                        )}
                        {description && (
                            <div className="citation-description">
                                <strong>Description: </strong> {description}
                            </div>
                        )}
                        {date && (
                            <div className="citation-date">
                                <strong>Date: </strong> {date}
                            </div>
                        )}
                        <div className="text-right">
                            <span className="notes-highlight notes-highlight-blue">
                                More Details <FontAwesomeIcon icon={faArrowRight} />
                            </span>
                        </div>
                    </Popover.Body>
                </Popover>
            );
        }

        // Fallback for when no citation data is provided
        return (
            <Popover id={`citation-popover-${citationNumber}`} className="citation-popover">
                <Popover.Header className="citation-popover-header">
                    Citation {citationNumber}
                </Popover.Header>
                <Popover.Body className="citation-popover-body">
                    Unknown Citation
                </Popover.Body>
            </Popover>
        );
    };

    return (
        <>
            <OverlayTrigger
                trigger={[]}
                placement="top"
                show={showPopover}
                overlay={renderPopoverContent()}
            >
                <span
                    className="citation-bubble citation-bubble-light"
                    role="button"
                    tabIndex={0}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={(e: any) => {
                        e.stopPropagation();
                        setSelectedCitationNumber(citationNumber);
                        setShowModal(true);
                    }}
                    onMouseDown={(e: any) => {
                        e.stopPropagation();
                    }}
                    onFocus={() => setShowPopover(true)}
                    onBlur={handleMouseLeave}
                    aria-label={`Citation ${citationNumber}`}
                >
                    {displayIndex}
                </span>
            </OverlayTrigger>
            {
                showModal && (
                    <CitationDetailsModal citationNumber={selectedCitationNumber} scanMulti={scanMulti} show={showModal} setShow={setShowModal} />
                )
            }
        </>
    );
};

const TimestampBubble: React.FC<{ timestamp: string }> = ({ timestamp }) => {
    const [useRelativeDate, setUseRelativeDate] = useState(true);
    return (
        <span
            className="citation-bubble timestamp-bubble"
            role="button"
            tabIndex={0}
            aria-label={`Timestamp ${timestamp}`}
            onClick={() => {
                setUseRelativeDate(!useRelativeDate);
            }}
        >
            {
                useRelativeDate && timestamp.replace(" PT", "") ? (
                    getRelativeDate(parseAsPacific(timestamp.replace(" PT", "")))
                ) : (
                    timestamp
                )
            }
        </span>
    );
};

export const LLMDashboardMarkdownRenderer: React.FC<LLMDashboardMarkdownRendererProps> = ({
    content, scanMulti
}) => {
    let processedContent;
    if (scanMulti) {
        processedContent = preprocessTimestamps(preprocessContentForCitations(content));
    } else {
        processedContent = preprocessTimestamps(preprocessContentForCitations(content, true));
    }

    // Local map to track sequential display numbers
    const citationDisplayMap: Record<number, number> = {};
    let displayCounter = 1;

    const components: Components = {
        a: ({ node, ...props }) => {
            const href = props.href || '';
            const title = props.title || '';
            const citationText = props.children?.toString() || '';

            // Check if this is a citation link (created by our preprocessing)
            if (title.startsWith('citation-')) {
                const originalCitationNumber = parseInt(title.replace('citation-', ''));
                const citationMatch = citationText.match(/^\[(\d+)\]$/);

                if (citationMatch && scanMulti) {
                    // Assign display index locally
                    if (!(originalCitationNumber in citationDisplayMap)) {
                        citationDisplayMap[originalCitationNumber] = displayCounter++;
                    }
                    const displayIndex = citationDisplayMap[originalCitationNumber];
                    const citation = scanMulti.citation_mapping ? scanMulti.citation_mapping[originalCitationNumber] : undefined;

                    // console.log(`originalCitationNumber=${originalCitationNumber}; displayIndex=${displayIndex}; scanMulti.citation_mapping=`, scanMulti.citation_mapping);

                    if (!citation) {
                        return <></>
                    }

                    return (
                        <CitationBubble
                            dataType={citation.data_type}
                            displayIndex={displayIndex.toString()}
                            citationNumber={originalCitationNumber.toString()}
                            citationMetadata={citation.metadata}
                            scanMulti={scanMulti}
                        />
                    );
                }
            }

            // Timestamp
            if (title.startsWith('timestamp-')) {
                const decoded = decodeURIComponent(title.replace('timestamp-', ''));
                return <TimestampBubble timestamp={decoded} />;
            }

            return (
                <a href={href} target="_blank" rel="noopener noreferrer">
                    {props.children}
                </a>
            );
        },
    };

    return (
        <ReactMarkdown
            components={components}
            remarkPlugins={[remarkGfm]}
            urlTransform={(uri) => uri}
        >
            {processedContent}
        </ReactMarkdown>
    );
};
